import React, { useCallback, useEffect, useRef } from "react";
import { InVisData } from "./invis-types";
import type { ViewTransform } from "./invis-types";
import type { EngineState } from "./invis-engine";

// --- Color scale: viridis (dark purple -> teal -> yellow) ---
const COLOR_STOPS = [
  [68, 1, 84],     // dark purple
  [59, 82, 139],   // blue
  [33, 145, 140],  // teal
  [94, 201, 98],   // green
  [253, 231, 37],  // yellow
];

function labelToColor(value: number, min: number, max: number): string {
  const t = max > min ? (value - min) / (max - min) : 0.5;
  const idx = t * (COLOR_STOPS.length - 1);
  const i = Math.min(Math.floor(idx), COLOR_STOPS.length - 2);
  const f = idx - i;
  const a = COLOR_STOPS[i];
  const b = COLOR_STOPS[i + 1];
  const r = Math.round(a[0] + f * (b[0] - a[0]));
  const g = Math.round(a[1] + f * (b[1] - a[1]));
  const bl = Math.round(a[2] + f * (b[2] - a[2]));
  return `rgb(${r},${g},${bl})`;
}

// Pre-compute colors for all labels
function buildColorPalette(labels: number[]): { colors: string[]; min: number; max: number } {
  const min = Math.min(...labels);
  const max = Math.max(...labels);
  const colors = labels.map((l) => labelToColor(l, min, max));
  return { colors, min, max };
}

interface InVisCanvasProps {
  data: InVisData;
  state: React.MutableRefObject<EngineState | null>;
  controlPoints: React.MutableRefObject<Map<number, { targetX: number; targetY: number }>>;
  renderTick: number;
  onToggleControlPoint: (index: number) => void;
  onMoveControlPoint: (index: number, x: number, y: number) => void;
  onAddControlPoint: (index: number, x: number, y: number) => void;
  onFinishDragging: () => void;
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
}

const POINT_RADIUS = 3.5;
const CP_RADIUS = 6;
const HIT_RADIUS = 10;

export const InVisCanvas: React.FC<InVisCanvasProps> = ({
  data,
  state,
  controlPoints,
  renderTick,
  onToggleControlPoint,
  onMoveControlPoint,
  onAddControlPoint,
  onFinishDragging,
  hoveredIndex,
  setHoveredIndex,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<ViewTransform>({ offsetX: 0, offsetY: 0, scale: 1 });
  const paletteRef = useRef<{ colors: string[]; min: number; max: number } | null>(null);

  // Interaction state
  const interactionRef = useRef<{
    mode: "idle" | "panning" | "dragging";
    dragIndex: number;
    lastMouseX: number;
    lastMouseY: number;
  }>({ mode: "idle", dragIndex: -1, lastMouseX: 0, lastMouseY: 0 });

  // Build color palette once
  if (!paletteRef.current && data) {
    paletteRef.current = buildColorPalette(data.labels);
  }

  // --- Coordinate transforms ---
  const screenToData = useCallback(
    (sx: number, sy: number, canvas: HTMLCanvasElement): [number, number] => {
      const v = viewRef.current;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dx = (sx - w / 2) / v.scale + v.offsetX;
      const dy = (sy - h / 2) / v.scale + v.offsetY;
      return [dx, dy];
    },
    []
  );

  const dataToScreen = useCallback(
    (dx: number, dy: number, canvas: HTMLCanvasElement): [number, number] => {
      const v = viewRef.current;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const sx = (dx - v.offsetX) * v.scale + w / 2;
      const sy = (dy - v.offsetY) * v.scale + h / 2;
      return [sx, sy];
    },
    []
  );

  // --- Hit test: find nearest point within HIT_RADIUS ---
  const hitTest = useCallback(
    (sx: number, sy: number): number => {
      const s = state.current;
      const canvas = canvasRef.current;
      if (!s || !canvas || !data) return -1;

      const [dx, dy] = screenToData(sx, sy, canvas);
      const v = viewRef.current;
      const hitRadiusData = HIT_RADIUS / v.scale;
      const hitRadiusSq = hitRadiusData * hitRadiusData;

      let bestIdx = -1;
      let bestDistSq = hitRadiusSq;

      for (let i = 0; i < data.n; i++) {
        const px = s.embedding[i * 2];
        const py = s.embedding[i * 2 + 1];
        const ddx = px - dx;
        const ddy = py - dy;
        const distSq = ddx * ddx + ddy * ddy;
        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          bestIdx = i;
        }
      }
      return bestIdx;
    },
    [data, state, screenToData]
  );

  // --- Auto-fit view to data bounds ---
  const autoFitView = useCallback(() => {
    const s = state.current;
    const canvas = canvasRef.current;
    if (!s || !canvas || !data) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < data.n; i++) {
      const x = s.embedding[i * 2];
      const y = s.embedding[i * 2 + 1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

    const padding = 0.1;
    const rangeX = (maxX - minX) || 1;
    const rangeY = (maxY - minY) || 1;

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    viewRef.current = {
      offsetX: (minX + maxX) / 2,
      offsetY: (minY + maxY) / 2,
      scale: Math.min(w / (rangeX * (1 + padding * 2)), h / (rangeY * (1 + padding * 2))),
    };
  }, [data, state]);

  // Store a draw ref so the ResizeObserver can trigger redraws
  const drawRef = useRef<(() => void) | null>(null);

  // --- Canvas resize handling ---
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const observer = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      const w = container.clientWidth;
      const h = container.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      // Redraw after canvas resize so initial load renders immediately
      drawRef.current?.();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Auto-fit view when data changes (initial load or kernel switch)
  const prevDataRef = useRef<InVisData | null>(null);
  const needsAutoFitRef = useRef(false);
  useEffect(() => {
    if (data && state.current && data !== prevDataRef.current) {
      // Rebuild color palette for new data
      paletteRef.current = buildColorPalette(data.labels);
      prevDataRef.current = data;
      needsAutoFitRef.current = true;
      // Try to auto-fit and draw now (works if ResizeObserver already fired)
      autoFitView();
      draw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // --- Drawing ---
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const s = state.current;
    if (!canvas || !s || !data || !paletteRef.current) return;
    // If we need to auto-fit (e.g. ResizeObserver fired after data loaded), do it now
    if (needsAutoFitRef.current) {
      needsAutoFitRef.current = false;
      autoFitView();
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const v = viewRef.current;
    const palette = paletteRef.current;
    const cp = controlPoints.current;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply DPR scaling
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Draw all points
    const pointRadius = POINT_RADIUS;
    const cpRadius = CP_RADIUS;

    for (let i = 0; i < data.n; i++) {
      const dx = s.embedding[i * 2];
      const dy = s.embedding[i * 2 + 1];
      const sx = (dx - v.offsetX) * v.scale + w / 2;
      const sy = (dy - v.offsetY) * v.scale + h / 2;

      // Skip points far outside viewport
      if (sx < -20 || sx > w + 20 || sy < -20 || sy > h + 20) continue;

      const isCP = cp.has(i);
      const isHovered = i === hoveredIndex;
      const r = isCP ? cpRadius : pointRadius;

      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fillStyle = palette.colors[i];
      ctx.fill();

      if (isCP) {
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#000";
        ctx.stroke();
        // White inner ring
        ctx.beginPath();
        ctx.arc(sx, sy, r + 2, 0, Math.PI * 2);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#fff";
        ctx.stroke();
      }

      if (isHovered && !isCP) {
        ctx.beginPath();
        ctx.arc(sx, sy, r + 3, 0, Math.PI * 2);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#000";
        ctx.stroke();
      }
    }

    // Draw control point targets (cross markers) if being dragged
    if (interactionRef.current.mode === "dragging") {
      cp.forEach(({ targetX, targetY }) => {
        const sx = (targetX - v.offsetX) * v.scale + w / 2;
        const sy = (targetY - v.offsetY) * v.scale + h / 2;
        ctx.beginPath();
        ctx.moveTo(sx - 5, sy);
        ctx.lineTo(sx + 5, sy);
        ctx.moveTo(sx, sy - 5);
        ctx.lineTo(sx, sy + 5);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#000";
        ctx.stroke();
      });
    }
  }, [data, state, controlPoints, hoveredIndex, autoFitView]);

  // Keep drawRef in sync so the ResizeObserver can trigger redraws
  drawRef.current = draw;

  // Redraw whenever renderTick or hoveredIndex changes
  useEffect(() => {
    draw();
  }, [renderTick, hoveredIndex, draw]);

  // --- Mouse event handlers ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button === 2) return; // right-click handled in contextmenu
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const cp = controlPoints.current;

      const hitIdx = hitTest(sx, sy);

      if (hitIdx >= 0 && cp.has(hitIdx)) {
        // Start dragging existing control point
        interactionRef.current = { mode: "dragging", dragIndex: hitIdx, lastMouseX: sx, lastMouseY: sy };
      } else {
        // Start panning
        interactionRef.current = { mode: "panning", dragIndex: -1, lastMouseX: sx, lastMouseY: sy };
      }
    },
    [hitTest, controlPoints]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const inter = interactionRef.current;

      if (inter.mode === "panning") {
        const v = viewRef.current;
        const ddx = (sx - inter.lastMouseX) / v.scale;
        const ddy = (sy - inter.lastMouseY) / v.scale;
        v.offsetX -= ddx;
        v.offsetY -= ddy;
        inter.lastMouseX = sx;
        inter.lastMouseY = sy;
        draw();
      } else if (inter.mode === "dragging") {
        const [dx, dy] = screenToData(sx, sy, canvas);
        onMoveControlPoint(inter.dragIndex, dx, dy);
        inter.lastMouseX = sx;
        inter.lastMouseY = sy;
      } else {
        // Hover detection
        const hitIdx = hitTest(sx, sy);
        setHoveredIndex(hitIdx >= 0 ? hitIdx : null);
      }
    },
    [draw, hitTest, onMoveControlPoint, screenToData, setHoveredIndex]
  );

  const handleMouseUp = useCallback(() => {
    if (interactionRef.current.mode === "dragging") {
      onFinishDragging();
    }
    interactionRef.current.mode = "idle";
  }, [onFinishDragging]);

  const handleMouseLeave = useCallback(() => {
    if (interactionRef.current.mode === "dragging") {
      onFinishDragging();
    }
    interactionRef.current.mode = "idle";
    setHoveredIndex(null);
  }, [setHoveredIndex, onFinishDragging]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;

      const hitIdx = hitTest(sx, sy);
      if (hitIdx >= 0) {
        onToggleControlPoint(hitIdx);
      }
    },
    [hitTest, onToggleControlPoint]
  );

  // Native wheel handler (non-passive, so preventDefault works)
  const handleWheelRef = useRef<((e: WheelEvent) => void) | null>(null);
  handleWheelRef.current = (e: WheelEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const v = viewRef.current;

    const [dx, dy] = screenToData(sx, sy, canvas);

    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    v.scale *= factor;

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    v.offsetX = dx - (sx - w / 2) / v.scale;
    v.offsetY = dy - (sy - h / 2) / v.scale;

    draw();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e: WheelEvent) => handleWheelRef.current?.(e);
    canvas.addEventListener("wheel", handler, { passive: false });
    return () => canvas.removeEventListener("wheel", handler);
  }, []);

  // --- Tooltip position ---
  const getTooltipPos = useCallback((): { x: number; y: number } | null => {
    const s = state.current;
    const canvas = canvasRef.current;
    if (!s || !canvas || hoveredIndex === null || hoveredIndex < 0) return null;

    const [sx, sy] = dataToScreen(
      s.embedding[hoveredIndex * 2],
      s.embedding[hoveredIndex * 2 + 1],
      canvas
    );
    return { x: sx, y: sy };
  }, [state, hoveredIndex, dataToScreen]);

  const tooltipPos = hoveredIndex !== null ? getTooltipPos() : null;

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "relative", cursor: interactionRef.current.mode === "panning" ? "grabbing" : "default" }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
      {/* Tooltip */}
      {tooltipPos && hoveredIndex !== null && data && (
        <div
          style={{
            position: "absolute",
            left: tooltipPos.x + 12,
            top: tooltipPos.y - 10,
            background: "rgba(0,0,0,0.85)",
            color: "#fff",
            padding: "6px 10px",
            borderRadius: 5,
            fontSize: 14,
            fontFamily: "'Figtree', sans-serif",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 10,
          }}
        >
          <strong>{data.names[hoveredIndex]}</strong>
          <span style={{ marginLeft: 8, opacity: 0.8 }}>
            rating: {data.labels[hoveredIndex].toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
};
